import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { NavbarIcon } from "../atoms/navbar/Icon";
import { StringCustomField } from "../components/CustomFieldForm";
import DeleteIconButton from "../components/DeleteIconButton";
import { SearchableDropdown } from "../components/SearchableDropdown";
import { Table } from "../components/Table";
import { openSnackbar } from "../context/SnackbarContext";
import { searchOrganizationSecrets } from "../hooks/useOrganizationSecrets";
import { searchOrganizationVariables } from "../hooks/useOrganizationVariables";
import { HeaderVariable, OrganizationSecret, OrganizationVariable } from "../models/types";
import { IColumnVisibility } from "../molecules/ColumnVisibility";
import { EmptyTable } from "../molecules/EmptyTable";
import { TableTextWithEllipsis } from "../molecules/TableTextWithEllipsis";
import { AddHeaderModal } from "./modals/AddHeaderModal";
import { OrganizationSecretDetailsModal } from "./modals/OrganizationSecretDetails";
import { OrganizationVariableDetailsModal } from "./modals/OrganizationVariableDetails";
import { RenderOrganizationVariableSecret } from "./RenderOrganizationVariableSecret";

type HeadersPorps = {
  value: string[] | null;
  onChange: (newValue: string[] | null) => void;
  title?: string;
  addTitle?: string;
  expandable?: boolean;
  border?: boolean;
  type?: "Variable" | "Secret";
  hideEmptyAction?: boolean;
  emptyLabel?: string;
  emptyDescription?: string;
};

export const Headers = (props: HeadersPorps) => {
  const {
    value,
    onChange,
    title = "Headers",
    addTitle = "Add New",
    expandable = false,
    border = false,
    type,
    emptyDescription,
    emptyLabel = "No Headers",
    hideEmptyAction,
  } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [isExpandable, setIsExpandable] = useState(expandable);
  const [expanded, setExpanded] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState<HeaderVariable | null>(null);
  const theme = useTheme();

  const columnVisibility: IColumnVisibility<HeaderVariable>[] = [
    {
      field: "key",
      headerName: "Key",
      visible: true,
      renderer: (row) => (
        <TableTextWithEllipsis
          onClick={() => {
            setSelectedForEdit(row);
            setModalOpen(true);
          }}
          value={row.key}
        />
      ),
    },
    {
      field: "value",
      headerName: "Value",
      visible: true,
      renderer: (row) => {
        return <RenderOrganizationVariableSecret type={row.type} value={row.value} />;
      },
    },
    {
      field: "id",
      headerName: "",
      visible: true,
      columnMinWidth: 50,
      columnMaxWidth: 50,
      renderer: (row) => (
        <Box display="flex" justifyContent="center">
          <DeleteIconButton
            deleteConfirmationDialogTitle="Remove Header"
            deleteConfirmationButtonDescription="Remove"
            deleteConfirmationDialogDescription={`Are you sure you want to remove ${row.key}?`}
            onDeleteConfirm={() => {
              const newValueJson = [...(value || [])];
              newValueJson.splice(parseInt(row.id), 1);
              onChange(newValueJson);
            }}
            filledButton={false}
          />
        </Box>
      ),
    },
  ];

  const onAdd = (newValue: string) => {
    try {
      const isUpdate = selectedForEdit !== null;
      if (isUpdate) {
        const newValueJson = [...(value || [])];
        newValueJson[parseInt(selectedForEdit.id)] = newValue;
        onChange(newValueJson);
      } else {
        const newValueJson = [...(value || []), newValue];
        onChange(newValueJson);
      }
    } catch (error) {
      openSnackbar("Failed to set header", "error");
    } finally {
      setSelectedForEdit(null);
    }
  };

  const rows = value?.map(
    (
      item,
      index
    ): {
      id: string;
      key: string;
      type: "Variable" | "Secret";
      value: OrganizationSecret | OrganizationVariable;
      secret: boolean;
    } => {
      const parsedItem = JSON.parse(item);
      return {
        id: index.toString(),
        key: parsedItem?.key,
        type: parsedItem?.type as "Variable" | "Secret",
        value: parsedItem?.value as OrganizationSecret | OrganizationVariable,
        secret: parsedItem?.secret,
      };
    }
  );

  return (
    <Box
      display="flex"
      width="100%"
      maxWidth="100%"
      flexDirection="column"
      gap="10px"
      sx={{
        ...(border && {
          border: `1px solid ${theme.palette.custom.secondaryBorder}`,
          borderRadius: "6px",
          padding: "10px",
        }),
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" flexDirection="row" alignItems={"center"} gap="10px">
          {isExpandable && (
            <Box
              sx={{ cursor: "pointer" }}
              onClick={() => setExpanded(!expanded)}
              alignItems="center"
              display="flex"
            >
              <NavbarIcon variant={expanded ? "chevron-up-sm" : "chevron-down-sm"} />
            </Box>
          )}
          <Typography
            variant="h3"
            sx={{
              ...(isExpandable && {
                cursor: "pointer",
              }),
            }}
            onClick={() => isExpandable && setExpanded(!expanded)}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          variant="small"
          onClick={() => setModalOpen(true)}
          color={theme.palette.custom.hyperlink}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              textDecoration: "none",
            },
          }}
        >
          {addTitle}
        </Typography>
      </Box>
      {((isExpandable && expanded) || !isExpandable) && (
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          maxWidth="100%"
          borderRadius="6px"
          border="1px solid"
          borderTop={0}
          overflow="auto"
          borderColor={theme.palette.custom.secondaryBorder}
        >
          <Table
            flex={1}
            borderTopRadius="6px"
            smallEmptyHeight={true}
            columnsVisibility={columnVisibility}
            isLoading={false}
            rows={rows || []}
            emptyTableComponent={
              <EmptyTable
                variant="empty-box"
                label={emptyLabel}
                description={emptyDescription}
                action={
                  hideEmptyAction
                    ? undefined
                    : () => {
                        setModalOpen(true);
                      }
                }
                actionLabel="Add"
              />
            }
          />
        </Box>
      )}
      <AddHeaderModal
        type={type}
        position="right"
        size="small"
        selectedForEdit={selectedForEdit}
        open={modalOpen}
        onClose={() => {
          setSelectedForEdit(null);
          setModalOpen(false);
        }}
        onAdd={onAdd}
      />
    </Box>
  );
};

type HeaderPorps = {
  value: string | null;
  onChange: (newValue: string | null) => void;
  type?: "Secret" | "Variable";
};

export const Header = (props: HeaderPorps) => {
  const { value, onChange, type } = props;
  const theme = useTheme();

  useEffect(() => {
    if (type) {
      handleChangeType(type);
    }
  }, [type]);

  const handleChangeKey = (newValue: string) => {
    const oldValue = JSON.parse(value || "{}");
    const newValueJson = { ...oldValue, key: newValue };
    onChange(JSON.stringify(newValueJson));
  };

  const handleChangeType = (newValue: "Variable" | "Secret") => {
    const oldValue = JSON.parse(value || "{}");
    const newValueJson = { ...oldValue, type: newValue, value: null };
    onChange(JSON.stringify(newValueJson));
  };

  const handleChangeValue = (newValue: OrganizationVariable | OrganizationSecret | null) => {
    const oldValue = JSON.parse(value || "{}");
    const newValueJson = { ...oldValue, value: newValue };
    onChange(JSON.stringify(newValueJson));
  };

  const getValue = (key: string) => {
    const valueJson = JSON.parse(value || "{}");
    return valueJson[key];
  };

  const [selectedVariable, setSelectedVariable] = useState<string | null>(null);
  const [openVariableModal, setOpenVariableModal] = useState<boolean>(false);
  const [selectedSecret, setSelectedSecret] = useState<string | null>(null);
  const [openSecretModal, setOpenSecretModal] = useState<boolean>(false);

  return (
    <Box display="flex" flexDirection="column" gap="10px">
      <OrganizationVariableDetailsModal
        objectId={selectedVariable}
        open={openVariableModal}
        onClose={() => {
          setOpenVariableModal(false);
          setSelectedVariable(null);
        }}
      />
      <OrganizationSecretDetailsModal
        objectId={selectedSecret}
        open={openSecretModal}
        onClose={() => {
          setOpenSecretModal(false);
          setSelectedSecret(null);
        }}
      />
      <Typography variant="h3" color={theme.palette.custom.secondaryTypography}>
        Key
      </Typography>
      <StringCustomField
        required={true}
        value={getValue("key")}
        onChange={handleChangeKey}
        error={false}
        errorMessage=""
      />
      {!type && (
        <>
          <Typography variant="h3" color={theme.palette.custom.secondaryTypography}>
            Type
          </Typography>
          <SearchableDropdown<"Variable" | "Secret">
            label={"Header type"}
            required={true}
            value={getValue("type")}
            onChange={(val: "Variable" | "Secret" | null) => handleChangeType(val ?? "Variable")}
            getOptions={async (searchText) => ["Variable", "Secret"]}
            isOptionEqualToValue={(a, b) => a === b}
            getOptionLabel={(option) => option}
          />
        </>
      )}
      <Typography variant="h3" color={theme.palette.custom.secondaryTypography}>
        Value
      </Typography>
      {getValue("type") !== "Secret" && (
        <SearchableDropdown<OrganizationVariable>
          label={"Value"}
          disabled={!getValue("type")}
          required={true}
          value={getValue("value")}
          onChange={handleChangeValue}
          getOptions={searchOrganizationVariables}
          isOptionEqualToValue={(a, b) => a === b}
          getOptionLabel={(option) => `${option.name}`}
          getSummaryDescription={(option) => [option.name, option.value]}
          onEdit={(orgVar) => {
            setSelectedVariable(orgVar.id);
            setOpenVariableModal(true);
          }}
          actionButton={["Create Variable", () => setOpenVariableModal(true)]}
        />
      )}
      {getValue("type") === "Secret" && (
        <SearchableDropdown<OrganizationSecret>
          label={"Value"}
          disabled={!getValue("type")}
          required={true}
          value={getValue("value")}
          onChange={handleChangeValue}
          getOptions={searchOrganizationSecrets}
          isOptionEqualToValue={(a, b) => a === b}
          getOptionLabel={(option) => `${option.name}`}
          getSummaryDescription={(option) => [option.name, ""]}
          onEdit={(orgVar) => {
            setSelectedSecret(orgVar.id);
            setOpenSecretModal(true);
          }}
          actionButton={["Create Secret", () => setOpenSecretModal(true)]}
        />
      )}
    </Box>
  );
};
